import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PageWrapper } from "../components";
import { BottomNav } from "../components/BottomNav";
import MicIframeNotice from "../components/MicIframeNotice";
import ModeSelector from "../components/audio/ModeSelector";
import AudioEntry from "../components/audio/AudioEntry";
import useAudioMode from "../hooks/useAudioMode";
import { 
  Moon, ChevronLeft, X, Play, Pause, Mic, Square,
  Check, ChevronRight, Volume2, Music, Brain,
  Sparkles, Heart, Loader2
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session steps
const STEPS = {
  THEME: 0,
  TAPPING: 1,
  RECORDING: 2,
  REFLECTION: 3,
  PLAYBACK: 4
};

export const NightSessionPage = () => {
  const navigate = useNavigate();
  const { nightNumber } = useParams();
  const nightNum = parseInt(nightNumber);

  const [step, setStep] = useState(STEPS.THEME);
  const [nightData, setNightData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tapping state
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [tappingComplete, setTappingComplete] = useState(false);

  // Recording state — defaults to "self" (record own voice) per
  // workshop feedback: people get more from the night audio when
  // they hear their own voice reciting each affirmation, line by
  // line. The Reclaim voice is still selectable as a fallback.
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingType, setRecordingType] = useState("self");
  const [recordingError, setRecordingError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Reflection state
  const [reflectionText, setReflectionText] = useState("");

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchNightData();
  }, [nightNum]);

  const fetchNightData = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/integration/night/${nightNum}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to load night");
      }

      const data = await response.json();
      setNightData(data);

      // Mark session as started
      await fetch(`${API_URL}/api/integration/night/${nightNum}/start`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      // If there's an existing recording, load it
      if (data.existing_recording?.audio_data) {
        setRecordingUrl(data.existing_recording.audio_data);
        setRecordingType(data.existing_recording.recording_type || "self");
      }

      // If there's an existing reflection, load it
      if (data.existing_reflection?.reflection_text) {
        setReflectionText(data.existing_reflection.reflection_text);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Tapping → per-affirmation recording flow ──────────────────────
  //
  // When recordingType === "self" (the default), the Tapping step
  // becomes a continuous-recording walk through every affirmation:
  //   1. User taps "Start" — single MediaRecorder begins capturing.
  //   2. Affirmation #1 appears. User reads it aloud, taps "Next".
  //   3. Recording continues uninterrupted as affirmation #2 appears.
  //   4. After the LAST affirmation, mediaRecorder.stop() fires; the
  //      resulting blob (one continuous take with natural pauses
  //      between affirmations) is base64'd and POSTed to the existing
  //      `/api/integration/recording` endpoint, then we route to
  //      Playback. No backend change required.
  //
  // The "default" (Reclaim AI voice) path is untouched — it still
  // skips the recording controls and auto-generates Michael's voice
  // when the tapping step completes.
  const finishSelfRecordingAndPlayback = async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      // No live recording to finalize — most likely the user toggled
      // off "self" mid-walk or skipped the start button. Fall through
      // to the AI voice fallback so playback always has audio.
      autoGenerateAndEnterPlayback();
      return;
    }
    // The onstop handler in startRecording() will populate
    // recordingBlob + recordingUrl. We wait for that, then save +
    // route to playback.
    const blobReady = new Promise((resolve) => {
      const original = recorder.onstop;
      recorder.onstop = (e) => {
        if (original) original(e);
        resolve();
      };
    });
    recorder.stop();
    setIsRecording(false);
    await blobReady;
    // Pull the latest blob from the ref since setRecordingBlob is async
    const actualMime = recorder.mimeType || "audio/webm";
    const blob = new Blob(audioChunksRef.current, { type: actualMime });
    try {
      const reader = new FileReader();
      const audioData = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const token = localStorage.getItem("reclaim_token");
      await fetch(`${API_URL}/api/integration/recording`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          night: nightNum,
          audio_data: audioData,
          recording_type: "self",
        }),
      });
      // Use the in-memory blob URL for playback — instant, no
      // server round-trip needed since the saved copy is on the
      // server for future visits.
      setRecordingUrl(URL.createObjectURL(blob));
      setRecordingBlob(blob);
      setTimeout(() => setStep(STEPS.PLAYBACK), 600);
    } catch (err) {
      console.error("[Night] save self-recording failed", err);
      toast.error("Could not save your recording — playing back from this device.");
      setRecordingUrl(URL.createObjectURL(blob));
      setTimeout(() => setStep(STEPS.PLAYBACK), 600);
    }
  };

  const handleNextAffirmation = () => {
    if (currentAffirmationIndex < nightData.affirmations.length - 1) {
      setCurrentAffirmationIndex(prev => prev + 1);
    } else {
      setTappingComplete(true);
      if (recordingType === "self" && isRecording) {
        // Finish the continuous self-recording, save, then playback.
        finishSelfRecordingAndPlayback();
      } else {
        // Reclaim AI voice path — auto-generate Michael's voice.
        autoGenerateAndEnterPlayback();
      }
    }
  };

  const autoGenerateAndEnterPlayback = async () => {
    // If a recording is already present (user visited before), skip
    // regeneration and go straight in.
    if (recordingUrl) {
      setTimeout(() => setStep(STEPS.PLAYBACK), 800);
      return;
    }
    const token = localStorage.getItem("reclaim_token");
    try {
      const res = await fetch(`${API_URL}/api/integration/generate-default-voice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ night: nightNum }),
      });
      const data = await res.json();
      if (res.ok && data.audio_url) {
        const fullUrl = data.audio_url.startsWith("/")
          ? `${API_URL}${data.audio_url}`
          : data.audio_url;
        setRecordingUrl(fullUrl);
        setRecordingBlob(null);
        setRecordingType("default");
      }
    } catch (e) {
      console.error("[Night] auto-generate voice failed", e);
    } finally {
      // Advance regardless — PlaybackScreen handles a null recordingUrl
      // gracefully (music will still play).
      setTimeout(() => setStep(STEPS.PLAYBACK), 800);
    }
  };

  const startRecording = async () => {
    try {
      setRecordingError(null);
      console.log("[Recording] Requesting microphone access...");
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const errMsg = "Your browser doesn't support audio recording. Please try using Safari on iPhone or Chrome on Android, or use the AI Voice option.";
        setRecordingError(errMsg);
        toast.error(errMsg);
        console.error("[Recording] getUserMedia not supported");
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log("[Recording] Got microphone stream:", stream);
      
      // Check for MediaRecorder support
      if (typeof MediaRecorder === 'undefined') {
        const errMsg = "MediaRecorder not supported in this browser. Try using the AI Voice option.";
        setRecordingError(errMsg);
        toast.error(errMsg);
        console.error("[Recording] MediaRecorder not defined");
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      
      // Determine best mimeType
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
          mimeType = 'audio/wav';
        } else {
          mimeType = ''; // Let browser decide
        }
      }
      console.log("[Recording] Using mimeType:", mimeType);
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log("[Recording] Chunk received:", event.data.size, "bytes");
        }
      };

      mediaRecorderRef.current.onstop = () => {
        console.log("[Recording] Stopped, processing chunks...");
        const actualMimeType = mediaRecorderRef.current.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        console.log("[Recording] Created blob:", blob.size, "bytes");
        setRecordingBlob(blob);
        setRecordingUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100); // Capture in 100ms chunks
      setIsRecording(true);
      toast.success("Recording started!");
      console.log("[Recording] Started recording");
    } catch (err) {
      console.error("[Recording] Error:", err);
      let errMsg;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errMsg = "Microphone access was blocked. Please tap the lock icon in your browser's address bar and allow microphone access, then try again.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        // On mobile, this usually means permissions weren't granted or browser restrictions
        errMsg = "Unable to access microphone. Please check your browser settings allow microphone access for this site. On iPhone, go to Settings > Safari > Microphone.";
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errMsg = "Microphone is being used by another app. Please close other apps using the microphone and try again.";
      } else if (err.name === 'OverconstrainedError') {
        errMsg = "Microphone settings issue. Please try again.";
      } else if (err.name === 'SecurityError') {
        errMsg = "Microphone access requires a secure connection (HTTPS).";
      } else {
        errMsg = "Could not access microphone. Please check your browser permissions and try again, or use the AI Voice option.";
      }
      setRecordingError(errMsg);
      toast.error(errMsg);
    }
  };

  const stopRecording = () => {
    console.log("[Recording] Stop requested, isRecording:", isRecording);
    if (mediaRecorderRef.current && isRecording) {
      console.log("[Recording] Stopping MediaRecorder...");
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success("Recording stopped!");
    }
  };

  const saveRecording = async () => {
    if (!recordingBlob && !recordingUrl) {
      toast.error("Please record your affirmations first");
      return;
    }

    const token = localStorage.getItem("reclaim_token");
    
    try {
      let audioData = recordingUrl;
      
      // Convert blob to base64 if it's a new recording
      if (recordingBlob) {
        const reader = new FileReader();
        audioData = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(recordingBlob);
        });
      }

      await fetch(`${API_URL}/api/integration/recording`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          night: nightNum,
          audio_data: audioData,
          recording_type: recordingType
        })
      });

      toast.success("Recording saved!");
      setStep(STEPS.REFLECTION);
    } catch (err) {
      toast.error("Failed to save recording");
    }
  };

  const saveReflection = async () => {
    const token = localStorage.getItem("reclaim_token");
    
    try {
      await fetch(`${API_URL}/api/integration/reflection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          night: nightNum,
          reflection_text: reflectionText
        })
      });

      toast.success("Reflection saved!");
      setStep(STEPS.PLAYBACK);
    } catch (err) {
      toast.error("Failed to save reflection");
    }
  };

  const completeNight = async () => {
    const token = localStorage.getItem("reclaim_token");
    
    try {
      const response = await fetch(`${API_URL}/api/integration/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ night: nightNum })
      });

      const data = await response.json();
      
      if (data.journey_complete) {
        toast.success("Congratulations! You've completed all 21 nights!");
      } else {
        toast.success(`Night ${nightNum} complete! Next night unlocks in 8 hours.`);
      }

      setIsPlaying(true);
      // User can now let it play while they sleep
    } catch (err) {
      toast.error("Failed to complete night");
    }
  };

  const handleExit = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    navigate("/integration");
  };

  if (loading) {
    return (
      <PageWrapper showNav={false}>
        <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper showNav={false}>
        <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] p-6">
          <button onClick={() => navigate("/integration")} className="flex items-center gap-2 text-indigo-300 mb-6">
            <ChevronLeft size={20} />
            Back
          </button>
          <div className="text-center py-12">
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper showNav={false}>
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] pb-20">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <button 
            onClick={handleExit}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
          <div className="flex items-center gap-2">
            <Moon className="w-5 h-5 text-indigo-400" />
            <span className="text-indigo-200/60 text-sm">Night {nightNum}</span>
          </div>
          <div className="w-10" /> {/* Spacer */}
        </div>

        {/* Step Indicators — 3 stages: Theme → Tapping → Audio.
            RECORDING (2) and REFLECTION (3) are retained in code for
            backwards-compat but are skipped in the new flow. */}
        <div className="px-6 mb-6">
          <div className="flex gap-2">
            {[STEPS.THEME, STEPS.TAPPING, STEPS.PLAYBACK].map((s) => (
              <div
                key={s}
                className={`flex-1 h-1 rounded-full transition-all ${
                  s <= step ? "bg-indigo-500" : "bg-white/10"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {step === STEPS.THEME && (
            <ThemeScreen
              key="theme"
              night={nightData}
              onContinue={() => setStep(STEPS.TAPPING)}
            />
          )}
          {step === STEPS.TAPPING && (
            <TappingScreen
              key="tapping"
              affirmations={nightData.affirmations}
              currentIndex={currentAffirmationIndex}
              onNext={handleNextAffirmation}
              isComplete={tappingComplete}
              recordingType={recordingType}
              onTypeChange={setRecordingType}
              isRecording={isRecording}
              recordingError={recordingError}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
            />
          )}
          {step === STEPS.RECORDING && (
            <RecordingScreen
              key="recording"
              nightNum={nightNum}
              affirmations={nightData.affirmations}
              isRecording={isRecording}
              recordingUrl={recordingUrl}
              recordingType={recordingType}
              recordingError={recordingError}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
              onSave={saveRecording}
              onTypeChange={setRecordingType}
              onAudioGenerated={(audioUrl) => {
                setRecordingUrl(audioUrl);
                setRecordingBlob(null);
              }}
            />
          )}
          {step === STEPS.REFLECTION && (
            <ReflectionScreen
              key="reflection"
              question={nightData.reflection_question}
              value={reflectionText}
              onChange={setReflectionText}
              onSave={saveReflection}
            />
          )}
          {step === STEPS.PLAYBACK && (
            <PlaybackScreen
              key="playback"
              nightNum={nightNum}
              recordingUrl={recordingUrl}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              onComplete={completeNight}
              onExit={handleExit}
              audioRef={audioRef}
            />
          )}
        </AnimatePresence>
        <BottomNav />
      </div>
    </PageWrapper>
  );
};

// Screen Components
const ThemeScreen = ({ night, onContinue }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="px-6 py-12 text-center"
  >
    <motion.div
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.2 }}
      className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                flex items-center justify-center mx-auto mb-8 shadow-lg shadow-indigo-500/30"
    >
      <span className="text-4xl font-bold text-white">{night.night}</span>
    </motion.div>

    <motion.h1
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
      className="text-3xl font-heading text-white mb-4"
    >
      {night.theme}
    </motion.h1>

    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4 }}
      className="text-indigo-200/70 mb-12 max-w-sm mx-auto leading-relaxed"
    >
      {night.description}
    </motion.p>

    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      onClick={onContinue}
      className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full
               text-white font-medium shadow-lg shadow-indigo-500/30
               hover:shadow-indigo-500/50 transition-all"
    >
      Begin Night Practice
    </motion.button>
  </motion.div>
);

const TappingScreen = ({
  affirmations,
  currentIndex,
  onNext,
  isComplete,
  recordingType,
  onTypeChange,
  isRecording,
  recordingError,
  onStartRecording,
  onStopRecording,
}) => {
  const isSelf = recordingType === "self";
  const lastAffirmation = currentIndex === affirmations.length - 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="px-6 py-8 flex flex-col items-center justify-center min-h-[60vh]"
    >
      {!isComplete ? (
        <>
          {/* Mode toggle — defaults to "Your voice" (self). One tap
              switches to Reclaim's pre-recorded voice for tonight. */}
          <div className="flex gap-2 justify-center mb-6">
            <button
              onClick={() => onTypeChange("self")}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-all ${
                isSelf
                  ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
                  : "bg-white/5 text-indigo-200/60"
              }`}
              data-testid="night-record-mode-self"
            >
              Your voice
            </button>
            <button
              onClick={() => onTypeChange("default")}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-all ${
                !isSelf
                  ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
                  : "bg-white/5 text-indigo-200/60"
              }`}
              data-testid="night-record-mode-default"
            >
              Reclaim voice
            </button>
          </div>

          <div className="text-center mb-6">
            <span className="text-indigo-400 text-sm">
              {currentIndex + 1} of {affirmations.length}
            </span>
          </div>

          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-8"
          >
            <p className="text-2xl text-white font-light leading-relaxed max-w-sm">
              "{affirmations[currentIndex]}"
            </p>
          </motion.div>

          {isSelf ? (
            // ─── Self-recording path ────────────────────────────────
            // The user records ALL affirmations as one continuous take
            // — mic stays on while they progress through each line.
            // This gives a natural night-meditation feel (their own
            // voice, natural pauses) and produces one blob the existing
            // backend endpoint already understands.
            <>
              {!isRecording ? (
                <motion.button
                  whileTap={{ scale: 0.94 }}
                  onClick={onStartRecording}
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-4
                             bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/40"
                  data-testid="night-record-start"
                >
                  <Mic className="w-9 h-9 text-white" />
                </motion.button>
              ) : (
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-4
                             bg-red-500/90 shadow-lg shadow-red-500/40"
                  aria-label="Recording in progress"
                >
                  <div className="w-3 h-3 rounded-full bg-white" />
                </motion.div>
              )}

              <p className={`text-sm mb-6 ${isRecording ? "text-red-400" : "text-indigo-200/60"}`}>
                {isRecording
                  ? lastAffirmation
                    ? "Read this last affirmation aloud, then tap Finish"
                    : "Read this affirmation aloud, then tap Next"
                  : "Tap the mic, then read each affirmation aloud"}
              </p>

              {recordingError && (
                <div className="w-full max-w-sm mb-4 p-3 bg-red-500/20 rounded-lg text-red-300 text-sm text-center">
                  {recordingError}
                </div>
              )}

              <button
                onClick={onNext}
                disabled={!isRecording}
                className={`px-6 py-3 rounded-full transition-all flex items-center gap-2 ${
                  isRecording
                    ? "bg-indigo-500/30 hover:bg-indigo-500/40 text-indigo-100"
                    : "bg-white/5 text-indigo-200/40 cursor-not-allowed"
                }`}
                data-testid="night-affirmation-next"
              >
                {lastAffirmation ? "Finish & Save" : "Next Affirmation"}
                <ChevronRight size={18} />
              </button>
            </>
          ) : (
            // ─── Reclaim AI voice path — original tapping flow ────
            <>
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="mb-6"
              >
                <Heart className="w-8 h-8 text-indigo-400/50" />
              </motion.div>

              <p className="text-indigo-200/50 text-sm mb-6">
                Tap your chest gently while reading
              </p>

              <button
                onClick={onNext}
                className="px-6 py-3 bg-indigo-500/20 hover:bg-indigo-500/30
                         rounded-full text-indigo-300 transition-all flex items-center gap-2"
                data-testid="night-affirmation-next"
              >
                Next Affirmation
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Sparkles className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
          <p className="text-xl text-white">Beautifully done</p>
          <p className="text-indigo-200/60 mt-2">
            {isSelf ? "Weaving your voice into tonight's audio…" : "Preparing your audio…"}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

// Default Reclaim Voice Section (Michael's voice)
const DefaultVoiceSection = ({ nightNum, onAudioGenerated }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    
    const token = localStorage.getItem("reclaim_token");
    
    try {
      // Use default Reclaim voice (Michael's voice)
      const response = await fetch(`${API_URL}/api/integration/generate-default-voice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ night: nightNum })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to load voice");
      }

      if (data.audio_url) {
        // Handle both relative and absolute URLs
        const fullUrl = data.audio_url.startsWith('/') 
          ? `${API_URL}${data.audio_url}` 
          : data.audio_url;
        onAudioGenerated(fullUrl);
        toast.success("Reclaim voice ready!");
      } else {
        throw new Error("No audio received");
      }
    } catch (err) {
      let errorMsg = err.message || "Failed to load Reclaim voice";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="text-center py-8">
      <p className="text-indigo-200/60 mb-4 max-w-sm mx-auto">
        Use Michael's voice to guide your subconscious integration tonight.
      </p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}
      
      <button
        onClick={handleGenerate}
        disabled={generating}
        className={`px-6 py-3 rounded-full transition-all flex items-center gap-2 mx-auto
          ${generating 
            ? "bg-indigo-500/30 text-indigo-300 cursor-wait" 
            : "bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30"
          }`}
      >
        {generating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading...
          </>
        ) : (
          <>
            <Volume2 className="w-5 h-5" />
            Use Reclaim Voice
          </>
        )}
      </button>
    </div>
  );
};

const RecordingScreen = ({ 
  nightNum,
  affirmations = [],
  isRecording, 
  recordingUrl, 
  recordingType,
  recordingError,
  onStartRecording, 
  onStopRecording, 
  onSave,
  onTypeChange,
  onAudioGenerated
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentReadingIndex, setCurrentReadingIndex] = useState(0);
  const audioRef = useRef(null);

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Auto-advance highlighted affirmation while recording
  useEffect(() => {
    let interval;
    if (isRecording && affirmations.length > 0) {
      interval = setInterval(() => {
        setCurrentReadingIndex(prev => 
          prev < affirmations.length - 1 ? prev + 1 : prev
        );
      }, 4000); // 4 seconds per affirmation
    }
    return () => clearInterval(interval);
  }, [isRecording, affirmations.length]);

  // Reset reading index when starting new recording
  useEffect(() => {
    if (isRecording) {
      setCurrentReadingIndex(0);
    }
  }, [isRecording]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="px-6 py-8"
    >
      <div className="text-center mb-6">
        <Mic className="w-10 h-10 text-indigo-400 mx-auto mb-3" />
        <h2 className="text-xl font-heading text-white mb-2">Record Your Affirmations</h2>
        <p className="text-indigo-200/60 text-sm max-w-sm mx-auto">
          Your voice has powerful influence over your subconscious mind.
        </p>
      </div>

      {/* Recording Type Toggle */}
      <div className="flex gap-2 justify-center mb-6">
        <button
          onClick={() => onTypeChange("default")}
          className={`px-4 py-2 rounded-full text-sm transition-all ${
            recordingType === "default"
              ? "bg-indigo-500 text-white"
              : "bg-white/5 text-indigo-200/60"
          }`}
        >
          Reclaim Voice
        </button>
        <button
          onClick={() => onTypeChange("self")}
          className={`px-4 py-2 rounded-full text-sm transition-all ${
            recordingType === "self"
              ? "bg-indigo-500 text-white"
              : "bg-white/5 text-indigo-200/60"
          }`}
        >
          Record Own Voice
        </button>
      </div>

      {recordingType === "self" ? (
        <div className="flex flex-col items-center">
          <div className="w-full max-w-sm mb-4">
            <MicIframeNotice accent="#A5B4FC" />
          </div>
          {/* Affirmations List - Read While Recording */}
          <div className="w-full max-w-sm mb-6 bg-white/5 rounded-xl p-4 max-h-48 overflow-y-auto">
            <p className="text-indigo-400/60 text-xs uppercase tracking-wider mb-3 text-center">
              Read these affirmations aloud
            </p>
            <div className="space-y-2">
              {affirmations.map((affirmation, index) => (
                <motion.div
                  key={index}
                  animate={{
                    backgroundColor: isRecording && index === currentReadingIndex 
                      ? "rgba(99, 102, 241, 0.2)" 
                      : "transparent",
                    scale: isRecording && index === currentReadingIndex ? 1.02 : 1
                  }}
                  className={`p-2 rounded-lg transition-all ${
                    isRecording && index === currentReadingIndex
                      ? "border border-indigo-500/50"
                      : index < currentReadingIndex && isRecording
                        ? "opacity-40"
                        : ""
                  }`}
                >
                  <p className={`text-sm ${
                    isRecording && index === currentReadingIndex
                      ? "text-white font-medium"
                      : "text-indigo-200/70"
                  }`}>
                    {index + 1}. "{affirmation}"
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Record Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={isRecording ? onStopRecording : onStartRecording}
            className={`w-20 h-20 rounded-full flex items-center justify-center mb-4
                      ${isRecording 
                        ? "bg-red-500 animate-pulse" 
                        : "bg-gradient-to-br from-indigo-500 to-purple-600"
                      } shadow-lg`}
          >
            {isRecording ? (
              <Square className="w-7 h-7 text-white" />
            ) : (
              <Mic className="w-9 h-9 text-white" />
            )}
          </motion.button>

          {isRecording ? (
            <p className="text-red-400 text-sm mb-4">Recording... Tap to stop</p>
          ) : (
            <p className="text-indigo-200/50 text-xs mb-4">Tap to start recording</p>
          )}

          {/* Error display */}
          {recordingError && !isRecording && (
            <div className="w-full max-w-sm mb-4 p-3 bg-red-500/20 rounded-lg text-red-300 text-sm text-center">
              {recordingError}
            </div>
          )}

          {/* Playback */}
          {recordingUrl && !isRecording && (
            <div className="w-full max-w-sm bg-white/5 rounded-xl p-4 mb-4">
              <audio 
                ref={audioRef} 
                src={recordingUrl}
                onEnded={() => setIsPlaying(false)}
              />
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlayback}
                  className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-indigo-400" />
                  ) : (
                    <Play className="w-5 h-5 text-indigo-400 ml-0.5" />
                  )}
                </button>
                <div className="flex-1">
                  <p className="text-white text-sm">Your Recording</p>
                  <p className="text-indigo-200/50 text-xs">Tap to preview</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <DefaultVoiceSection 
          nightNum={nightNum}
          onAudioGenerated={onAudioGenerated}
        />
      )}

      {/* Save Button */}
      {recordingUrl && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8"
        >
          <button
            onClick={onSave}
            className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 
                     rounded-full text-white font-medium"
          >
            Save & Continue
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

const ReflectionScreen = ({ question, value, onChange, onSave }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="px-6 py-8"
  >
    <div className="text-center mb-8">
      <Sparkles className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
      <h2 className="text-xl font-heading text-white mb-2">Gentle Reflection</h2>
      <p className="text-indigo-200/60 text-sm">
        Before sleep, reflect on this question
      </p>
    </div>

    <div className="bg-white/5 rounded-2xl p-6 mb-6">
      <p className="text-lg text-white italic text-center">
        "{question}"
      </p>
    </div>

    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Write your reflection..."
      className="w-full h-32 bg-white/5 border border-indigo-500/20 rounded-xl p-4
               text-white placeholder-indigo-200/30 resize-none focus:outline-none
               focus:border-indigo-500/50 transition-colors"
    />

    <button
      onClick={onSave}
      className="w-full py-4 mt-6 bg-gradient-to-r from-indigo-500 to-purple-600 
               rounded-full text-white font-medium"
    >
      Save Reflection
    </button>

    <button
      onClick={onSave}
      className="w-full py-3 mt-3 text-indigo-300/60 text-sm"
    >
      Skip for tonight
    </button>
  </motion.div>
);

const PlaybackScreen = ({
  nightNum,
  recordingUrl,
  isPlaying,
  setIsPlaying,
  onComplete,
  onExit,
  audioRef
}) => {
  // Global mode (Clear / Soft / Deep) — Night sessions default to Deep
  const [mode, setMode] = useAudioMode();

  // Audio refs
  const affirmationAudioRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const [audioError, setAudioError] = useState(null);

  // Web Audio graph for the affirmation — iOS Safari ignores `.volume`
  // on HTMLAudioElement, so we route through a GainNode + optional
  // lowpass BiquadFilter to enforce the per-mode voice level.
  const audioCtxRef = useRef(null);
  const voiceGainRef = useRef(null);
  const voiceFilterRef = useRef(null);
  const voiceSourceRef = useRef(null);

  const ensureVoiceGraph = () => {
    if (!affirmationAudioRef.current) return;
    if (audioCtxRef.current) return; // already wired
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const src = ctx.createMediaElementSource(affirmationAudioRef.current);
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 22000; // bypass by default
      src.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      voiceSourceRef.current = src;
      voiceGainRef.current = gain;
      voiceFilterRef.current = filter;
    } catch (e) {
      console.warn("[Night] Web Audio graph init failed, falling back to .volume", e);
    }
  };

  const applyVoiceProfile = (targetVolume, filterKind) => {
    // Direct volume set (works on desktop + Android; no-op on iOS)
    if (affirmationAudioRef.current) {
      affirmationAudioRef.current.volume = targetVolume;
    }
    // Web Audio override — the real iOS fix
    if (voiceGainRef.current) {
      try {
        const ctx = audioCtxRef.current;
        voiceGainRef.current.gain.cancelScheduledValues(ctx.currentTime);
        voiceGainRef.current.gain.setValueAtTime(voiceGainRef.current.gain.value, ctx.currentTime);
        voiceGainRef.current.gain.linearRampToValueAtTime(targetVolume, ctx.currentTime + 0.4);
      } catch (e) {}
    }
    if (voiceFilterRef.current) {
      // lowpass for Deep = 1800Hz (muffled), otherwise effectively open
      voiceFilterRef.current.frequency.value = filterKind === "lowpass" ? 1800 : 22000;
    }
  };

  // Pre-fetched per-mode configs and music URLs (so taps are instant + iOS-safe)
  const [modeConfigs, setModeConfigs] = useState({}); // { clear, soft, deep }
  const [modeMusicUrls, setModeMusicUrls] = useState({});
  const [warmingUp, setWarmingUp] = useState(true);

  // Pre-fetch all 3 modes upfront — this is what makes "tap = enter" feel instant
  useEffect(() => {
    let cancelled = false;
    const warm = async () => {
      try {
        const ids = ["clear", "soft", "deep"];
        const results = await Promise.all(
          ids.map((m) =>
            fetch(`${API_URL}/api/unified-audio/resolve?area=integration_program_night&mode=${m}`)
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );
        if (cancelled) return;
        const cfgs = {};
        const urls = {};
        ids.forEach((m, i) => {
          const data = results[i] || null;
          cfgs[m] = data;
          const u = data?.music?.url;
          urls[m] = u ? (u.startsWith("http") ? u : `${API_URL}${u}`) : null;
        });
        setModeConfigs(cfgs);
        setModeMusicUrls(urls);
      } finally {
        if (!cancelled) setWarmingUp(false);
      }
    };
    warm();
    return () => { cancelled = true; };
  }, []);

  // iOS-CRITICAL: play() must run synchronously inside the user-gesture handler.
  // Mode tap is the gesture — we use the pre-warmed config + URL for that mode.
  const handleModeSelect = (picked) => {
    setMode(picked);
    const cfg = modeConfigs[picked];
    const musicUrl = modeMusicUrls[picked];
    const voiceVol = cfg?.voice?.volume ?? (picked === "deep" ? 0.08 : 1.0);
    const voiceFilter = cfg?.voice?.filter || (picked === "deep" ? "lowpass" : null);

    // Wire the Web Audio graph BEFORE the first play() — iOS requires the
    // graph nodes to exist inside the same gesture to route volume changes.
    ensureVoiceGraph();
    // Resume context (iOS starts it suspended until a user gesture).
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }

    // Affirmations
    if (affirmationAudioRef.current && recordingUrl) {
      affirmationAudioRef.current.loop = true;
      applyVoiceProfile(voiceVol, voiceFilter);
      affirmationAudioRef.current.play().catch((e) => {
        console.error("[Night] affirmation play error", e);
        setAudioError("Could not play affirmations — please try again");
      });
    }
    // Music
    if (ambientAudioRef.current && musicUrl && (cfg?.music_volume ?? 0) > 0) {
      ambientAudioRef.current.src = musicUrl;
      ambientAudioRef.current.loop = true;
      ambientAudioRef.current.volume = cfg?.music_volume ?? 0.65;
      ambientAudioRef.current.play().catch((e) => {
        console.error("[Night] music play error", e);
      });
    }

    onComplete();
  };

  const handleStopPlayback = () => {
    if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
    if (ambientAudioRef.current) ambientAudioRef.current.pause();
    setIsPlaying(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
      if (ambientAudioRef.current) ambientAudioRef.current.pause();
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="px-6 py-10"
    >
      {/* Hidden Audio Elements — playsInline is critical for iOS.
          crossOrigin is required for MediaElementAudioSourceNode (used
          by Web Audio gain node for iOS volume override) to work with
          cross-origin audio URLs. */}
      <audio
        ref={affirmationAudioRef}
        src={recordingUrl}
        crossOrigin="anonymous"
        preload="auto"
        playsInline
        webkit-playsinline="true"
        onError={(e) => {
          console.error("Affirmation audio error:", e);
          setAudioError("Error loading affirmations audio");
        }}
      />
      <audio ref={ambientAudioRef} crossOrigin="anonymous" preload="auto" playsInline webkit-playsinline="true" />

      {audioError && (
        <div className="mb-4 p-3 bg-red-500/20 rounded-lg text-red-300 text-sm text-center max-w-md mx-auto">
          {audioError}
        </div>
      )}

      {!isPlaying ? (
        <AudioEntry
          title={`Night ${nightNum}`}
          subtitle="Sleep Integration"
          defaultMode="deep"
          selected={mode}
          onSelect={handleModeSelect}
          busy={warmingUp}
          area="integration_program_night"
        />
      ) : (
        <div className="text-center max-w-md mx-auto">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/20 rounded-full mb-4">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400">Playing — Night {nightNum} Complete</span>
          </div>
          <p className="text-indigo-200/50 text-sm mb-4">
            Playback will continue while you sleep
          </p>
          <button
            onClick={handleStopPlayback}
            className="px-6 py-2 bg-white/10 rounded-full text-white/70 text-sm mr-4"
          >
            Stop Playback
          </button>
          <button
            onClick={onExit}
            className="text-indigo-300/60 text-sm"
          >
            Close & Continue Playing
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default NightSessionPage;
